import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

/**
 * החלפות משמרת (PRD 5.4.7). כל פעולה עוברת ב RPC ולא בכתיבה ישירה,
 * כי שם יושבות הבדיקות (אותו סניף, לא עובד כבר באותו יום, אין אילוץ
 * חוסם) וההתראות.
 */
export type SwapStatus = 'open' | 'accepted' | 'approved' | 'rejected' | 'cancelled';

export type SwapRequest = {
  id: string;
  assignment_id: string;
  requester_id: string;
  target_employee_id: string | null;
  accepted_by_id: string | null;
  status: SwapStatus;
  note: string | null;
  created_at: string;
  shift_assignments: {
    date: string;
    branch_id: string;
    shift_types: { name: string; start_time: string; end_time: string } | null;
    branches: { name: string } | null;
  } | null;
  requester: { first_name: string; last_name: string } | null;
  accepted_by: { first_name: string; last_name: string } | null;
  target: { first_name: string; last_name: string } | null;
};

const SELECT = `
  id, assignment_id, requester_id, target_employee_id, accepted_by_id, status, note, created_at,
  shift_assignments ( date, branch_id, shift_types ( name, start_time, end_time ), branches ( name ) ),
  requester:employees!shift_swap_requests_requester_id_fkey ( first_name, last_name ),
  accepted_by:employees!shift_swap_requests_accepted_by_id_fkey ( first_name, last_name ),
  target:employees!shift_swap_requests_target_employee_id_fkey ( first_name, last_name )
`;

export const SWAPS_KEY = ['swaps'] as const;

/**
 * כל הבקשות שהעובד רשאי לראות. ה RLS כבר מצמצם להצעות שלו, להצעות
 * שהופנו אליו, ולהצעות פתוחות בסניף שלו, אז אין כאן סינון נוסף.
 */
export function useSwaps() {
  return useQuery({
    queryKey: SWAPS_KEY,
    queryFn: async (): Promise<SwapRequest[]> => {
      const { data, error } = await supabase
        .from('shift_swap_requests')
        .select(SELECT)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as SwapRequest[];
    },
  });
}

export function useSwapMutations() {
  const queryClient = useQueryClient();
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: SWAPS_KEY });
    void queryClient.invalidateQueries({ queryKey: ['my-week'] });
    void queryClient.invalidateQueries({ queryKey: ['notifications'] });
  };

  const request = useMutation({
    mutationFn: async (input: { assignmentId: string; targetId: string | null; note: string }) => {
      const { error } = await supabase.rpc('request_swap', {
        p_assignment: input.assignmentId,
        p_target: input.targetId,
        p_note: input.note || null,
      } as never);
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  const accept = useMutation({
    mutationFn: async (requestId: string) => {
      const { error } = await supabase.rpc('accept_swap', { p_request: requestId } as never);
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  const cancel = useMutation({
    mutationFn: async (requestId: string) => {
      const { error } = await supabase.rpc('cancel_swap', { p_request: requestId } as never);
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  const decide = useMutation({
    mutationFn: async (input: { requestId: string; approve: boolean }) => {
      const { error } = await supabase.rpc('decide_swap', {
        p_request: input.requestId,
        p_approve: input.approve,
      } as never);
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  return { request, accept, cancel, decide };
}

/** עובדי הסניף, להצעה אישית. */
export function useBranchStaff(branchId: string | undefined) {
  return useQuery({
    queryKey: ['branch-staff', branchId],
    enabled: Boolean(branchId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('employees')
        .select('id, first_name, last_name')
        .eq('is_active', true)
        .eq('is_support', false)
        .eq('primary_branch_id', branchId as string)
        .order('first_name');
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 5 * 60 * 1000,
  });
}

export const swapStatusLabel: Record<SwapStatus, string> = {
  open: 'מחכה למישהו שייקח',
  accepted: 'ממתין לאישור המנהל/ת',
  approved: 'אושרה',
  rejected: 'נדחתה',
  cancelled: 'בוטלה',
};
